import { Link } from 'react-router-dom';
import {
  HelpCircle, Settings, Calendar, AlertTriangle,
  Clock, MessageSquareDot, Cloud, Mail, ArrowRight, Layers
} from 'lucide-react';
import { company } from '@/data/company';

const HilfePage = () => {
  const supportEmail = company?.email || 'schuchardt@foerderfuchs-bildung.de';
  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Hilfe & Anleitung</h1>
        <p className="text-gray-500 mb-8">So nutzt du den Förderfuchs KI Social Media Manager</p>

        <div className="space-y-8">

          {/* Bluesky verbinden — der Kanal, der SOFORT funktioniert */}
          <section className="border-l-4 border-sky-500 pl-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-3">
              <Cloud className="text-sky-500" size={24} />
              Bluesky verbinden (in der Testphase kostenlos)
            </h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-600">
              <li>Gehe zu <Link to="/kanale" className="text-orange-600 hover:underline">Kanäle verbinden</Link></li>
              <li>Klicke bei <strong>Bluesky</strong> auf <strong>"Verbinden"</strong></li>
              <li>Noch kein Bluesky-Konto? In ~1 Minute kostenlos unter <a href="https://bsky.app" target="_blank" rel="noreferrer" className="text-orange-600 hover:underline">bsky.app</a> registrieren</li>
              <li>In Bluesky: <strong>Einstellungen → App-Passwörter → Hinzufügen</strong> und das angezeigte Passwort kopieren</li>
              <li>Handle (z. B. <em>name.bsky.social</em>) und App-Passwort im Fenster eintragen → <strong>Verbunden</strong></li>
            </ol>
            <div className="mt-3 p-3 bg-sky-50 rounded-lg text-sm text-sky-800">
              <strong>Wichtig:</strong> Das App-Passwort ist nicht dein normales Login-Passwort. Es wird in Bluesky eigens erstellt und nur einmal angezeigt.
            </div>
          </section>

          {/* Weitere Kanäle — ehrlich: in Freischaltung */}
          <section className="border-l-4 border-violet-500 pl-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-3">
              <Clock className="text-violet-500" size={24} />
              Facebook, Instagram, Threads, TikTok & Google
            </h2>
            {/* DOC-01 (16.08.2026): Hier stand LinkedIn in der Aufzählung der noch
                nicht verbindbaren Kanäle — im direkten Widerspruch zur Preis-FAQ
                („Heute direkt verbindbar: Bluesky und LinkedIn.") und zum Istzustand
                (CHANNEL_LIVE_LINKEDIN ist gesetzt). Zwei kundensichtbare Seiten, zwei
                gegensätzliche Aussagen — und die Werbung zeigt bald auf beide. */}
            <p className="text-gray-600">
              Diese Kanäle werden gerade offiziell freigeschaltet (Prüfprozess der Plattformen).
              Sobald sie bereit sind, verbindest du sie mit deinem gekauften Paket über den
              <strong> Verbindungsassistenten</strong>. Bis dahin kannst du deine Beiträge im
              <Link to="/monthly-plan" className="text-orange-600 hover:underline"> Monatsplan</Link> vorbereiten
              und über <strong>LinkedIn</strong> und <strong>Bluesky</strong> veröffentlichen.
            </p>
          </section>

          {/* Automatisch veröffentlichen */}
          <section className="border-l-4 border-orange-500 pl-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-3">
              <Clock className="text-orange-500" size={24} />
              Automatisch veröffentlichen
            </h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-600">
              <li>Verbinde deinen Kanal (in der Testphase Bluesky)</li>
              <li>Förderfuchs erstellt deinen Monatsplan automatisch</li>
              <li>Bei aktivem Auto-Veröffentlichen gehen die Beiträge zur geplanten Zeit ohne weiteren Klick live</li>
              <li>Den Plan siehst und bearbeitest du jederzeit im <Link to="/monthly-plan" className="text-orange-600 hover:underline">Monatsplan</Link></li>
            </ol>
          </section>

          {/* Karussells, die gelesen werden — Playbook für Kunden */}
          <section className="border-l-4 border-amber-500 pl-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-3">
              <Layers className="text-amber-500" size={24} />
              Karussells, die gelesen werden
            </h2>
            <p className="text-gray-600 mb-4">
              Ein Karussell ist ein Beitrag aus mehreren Folien, durch die man wischt. Mit der
              richtigen Reihenfolge wird es gelesen — hier der Kurz-Leitfaden.
            </p>

            <h3 className="font-semibold text-gray-800 mb-1">Wann ein Karussell (statt einem einzelnen Bild)?</h3>
            <p className="text-gray-600 mb-2">
              Nimm ein Karussell, wenn du einen Gedanken in Schritten erklärst:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600 mb-2">
              <li>eine Anleitung („In 5 Schritten zu …")</li>
              <li>eine Liste („3 Fehler, die …" / „4 Dinge, die du wissen musst")</li>
              <li>ein Aha-Moment, den du aufbaust, oder ein Vorher/Nachher</li>
            </ul>
            <p className="text-gray-600 mb-4">
              Für eine schnelle Ankündigung, ein Zitat oder ein einzelnes Foto reicht ein normaler
              Beitrag. Ein Karussell nur, wenn es wirklich mehrere Punkte gibt.
            </p>

            <h3 className="font-semibold text-gray-800 mb-1">Die bewährte Reihenfolge der Folien</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-600 mb-4">
              <li>
                <strong>Hook (Folie 1):</strong> Ein Satz, der neugierig macht oder ein Problem
                benennt. Kurz, keine Einleitung. Beispiel: „3 Fördertöpfe, die fast niemand kennt."
              </li>
              <li>
                <strong>Kontext (Folie 2):</strong> Kurz einordnen, worum es geht und für wen — damit
                der Leser weiß, ob sich das Weiterblättern lohnt.
              </li>
              <li>
                <strong>Inhalt (Folie 3 bis 5):</strong> Deine Punkte, eine Idee pro Folie. Nicht zwei
                Gedanken auf eine Folie quetschen. Lieber eine Folie mehr.
              </li>
              <li>
                <strong>Fazit (vorletzte Folie):</strong> Die Kernaussage in einem Satz — das
                Wichtigste zum Mitnehmen.
              </li>
              <li>
                <strong>Call-to-Action (letzte Folie):</strong> Sag genau, was jetzt zu tun ist.
                „Schreib deine Frage in die Kommentare." / „Sichere dir das kostenlose
                Erstgespräch."
              </li>
            </ol>

            <div className="mt-3 p-3 bg-amber-50 rounded-lg text-sm text-amber-900">
              <strong>Eine Idee pro Folie — die wichtigste Regel:</strong> Jede Folie sagt eine Sache.
              Wenn du beim Lesen ein „und" oder einen zweiten Gedanken bemerkst, mach zwei Folien
              daraus. Faustregel: Jede Folie muss in unter drei Sekunden erfassbar sein.
            </div>

            <h3 className="font-semibold text-gray-800 mb-1 mt-4">Sauberes Layout</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-600 mb-4">
              <li><strong>Kurz halten:</strong> wenige Worte pro Folie, keine ganzen Absätze. Eine Folie ist ein Plakat, kein Text.</li>
              <li><strong>Ruhig bleiben:</strong> nicht jede Folie vollpacken — Luft um den Text wirkt hochwertig.</li>
              <li><strong>Roter Faden:</strong> gleicher Stil, gleiche Farben auf allen Folien (macht das System automatisch für dich).</li>
              <li><strong>Reihenfolge zählt:</strong> Folie 1 muss zum Weiterblättern verführen, die letzte zur Handlung führen.</li>
            </ul>

            <h3 className="font-semibold text-gray-800 mb-1">Länge</h3>
            <p className="text-gray-600 mb-4">
              3 bis 5 Inhaltsfolien sind der Sweet Spot. Weniger wirkt dünn, mehr wird selten zu Ende
              geblättert. Mit Hook und Call-to-Action sind das rund 5 bis 7 Folien.
            </p>

            <h3 className="font-semibold text-gray-800 mb-1">Funktioniert auf LinkedIn und Instagram</h3>
            <p className="text-gray-600">
              Dieselbe Struktur passt für beide. Auf LinkedIn darf der Ton fachlicher sein, auf
              Instagram lockerer — der Aufbau (Hook, Inhalt, Handlung) bleibt gleich.
            </p>
          </section>

          {/* Fehler & Logs */}
          <section className="border-l-4 border-red-500 pl-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-3">
              <AlertTriangle className="text-red-500" size={24} />
              Fehler & Logs anzeigen
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-600">
              <li><strong>Monatsplan:</strong> Fehlgeschlagene Posts zeigen ein rotes <span className="text-red-600">❌ Fehlgeschlagen</span> Badge mit Fehlergrund als Tooltip</li>
              <li><strong>Wir sagen Bescheid:</strong> Kann ein Beitrag dauerhaft nicht veröffentlicht werden, bekommst du automatisch eine E-Mail mit dem betroffenen Kanal</li>
            </ul>
            <div className="mt-3 p-3 bg-red-50 rounded-lg text-sm text-red-800">
              <strong>Häufige Ursachen:</strong>
              <ul className="list-disc list-inside mt-2">
                <li>Verbindung abgelaufen → Kanal unter <Link to="/kanale" className="underline">Kanäle</Link> neu verbinden</li>
                <li>Kanal noch in Freischaltung → in der Testphase über Bluesky veröffentlichen</li>
              </ul>
            </div>
          </section>

          {/* Kommentar → DM */}
          <section className="border-l-4 border-pink-500 pl-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-3">
              <MessageSquareDot className="text-pink-500" size={24} />
              Kommentar → DM einrichten
            </h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-600">
              <li>
                Gehe zu{' '}
                <Link to="/keyword-dm" className="text-orange-600 hover:underline">
                  Kommentar → DM
                </Link>{' '}
                in der Navigation
              </li>
              <li>
                Trage unter <strong>„Neue Regel"</strong> ein Keyword ein (z. B. „Infos") und den
                Text, den die App als private Antwort senden soll
              </li>
              <li>
                Schalte die Regel auf <strong>Aktiv</strong> und klicke auf{' '}
                <strong>„Regel speichern"</strong>
              </li>
              <li>
                Sobald jemand deinen Keyword-Begriff unter einem deiner Instagram-Beiträge
                kommentiert, sendet die App automatisch eine private Antwort
              </li>
              <li>
                Im <strong>Aktivitäts-Log</strong> auf derselben Seite siehst du jeden
                verarbeiteten Kommentar mit Status (empfangen / Keyword gefunden / DM gesendet /
                Fehler / übersprungen) und den Zeitstempel
              </li>
            </ol>
            <div className="mt-3 p-3 bg-pink-50 rounded-lg text-sm text-pink-800">
              <strong>Hinweis:</strong> Pro Kommentar wird genau eine private Antwort gesendet.
              Eigene Kommentare des verbundenen Kontos werden automatisch übersprungen.{' '}
              <Link to="/instagram-comment-dm" className="underline font-medium">
                Mehr zum Feature →
              </Link>
            </div>
          </section>

          {/* Support — echter Kontaktpfad */}
          <section className="border-l-4 border-green-500 pl-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-3">
              <Mail className="text-green-600" size={24} />
              Noch Fragen? Wir helfen dir
            </h2>
            <p className="text-gray-600 mb-3">
              Du kommst nicht weiter oder etwas funktioniert nicht wie erwartet? Schreib uns —
              wir melden uns persönlich.
            </p>
            <a
              href={`mailto:${supportEmail}?subject=Support-Anfrage%20Förderfuchs%20Social%20Media%20Manager`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700"
            >
              <Mail size={16} /> {supportEmail} <ArrowRight size={14} />
            </a>
            <p className="text-xs text-gray-400 mt-2">
              Alternativ über unser <Link to="/kontakt" className="text-orange-600 hover:underline">Kontaktformular</Link>.
            </p>
          </section>

          {/* Quick Links */}
          <section className="bg-gray-50 rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4">Schnellzugriff</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/settings" className="flex flex-col items-center p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Settings className="text-gray-600 mb-2" size={24} />
                <span className="text-sm">Einstellungen</span>
              </Link>
              <Link to="/monthly-plan" className="flex flex-col items-center p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Calendar className="text-gray-600 mb-2" size={24} />
                <span className="text-sm">Monatsplan</span>
              </Link>
              <Link to="/keyword-dm" className="flex flex-col items-center p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <MessageSquareDot className="text-pink-500 mb-2" size={24} />
                <span className="text-sm">Kommentar → DM</span>
              </Link>
              <Link to="/datenschutz" className="flex flex-col items-center p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <HelpCircle className="text-gray-600 mb-2" size={24} />
                <span className="text-sm">Datenschutz</span>
              </Link>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default HilfePage;
